import { useRef, useMemo } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

// Register a Video blot so YouTube/Vimeo embeds also work
const BlockEmbed = Quill.import("blots/block/embed");
class VideoBlot extends BlockEmbed {
    static create(url) {
        const node = super.create();
        node.setAttribute("src", url);
        node.setAttribute("frameborder", "0");
        node.setAttribute("allowfullscreen", "true");
        node.setAttribute("class", "quill-video");
        return node;
    }
    static value(node) { return node.getAttribute("src"); }
}
VideoBlot.blotName = "video";
VideoBlot.tagName = "iframe";
try { Quill.register(VideoBlot, true); } catch { /* already registered */ }

/**
 * Rich text editor for blog body. Stores HTML.
 * Supports inline image uploads (POST /api/files/upload) and video embeds (YouTube / Vimeo).
 */
export default function RichTextEditor({ value, onChange, testid = "rich-text" }) {
    const quillRef = useRef(null);

    const uploadImage = () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*,video/*");
        input.click();
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) { toast.error("Max file size is 10 MB."); return; }
            const fd = new FormData();
            fd.append("file", file);
            const tId = toast.loading("Uploading…");
            try {
                const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const url = fileUrl(data.url || data.storage_path || data.path);
                const editor = quillRef.current?.getEditor?.();
                if (!editor) return;
                const range = editor.getSelection(true);
                if (file.type.startsWith("video/")) {
                    editor.insertEmbed(range.index, "video", url, "user");
                } else {
                    editor.insertEmbed(range.index, "image", url, "user");
                }
                editor.setSelection(range.index + 1);
                toast.success("Inserted.", { id: tId });
            } catch (err) {
                toast.error("Upload failed. Try a smaller file.", { id: tId });
            }
        };
    };

    const embedVideo = () => {
        const url = window.prompt("Paste a YouTube or Vimeo URL:");
        if (!url) return;
        let embedUrl = url;
        const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
        const vimeo = url.match(/vimeo\.com\/(\d+)/);
        if (yt) embedUrl = `https://www.youtube.com/embed/${yt[1]}`;
        else if (vimeo) embedUrl = `https://player.vimeo.com/video/${vimeo[1]}`;
        else { toast.error("Only YouTube and Vimeo URLs are supported."); return; }
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, "video", embedUrl, "user");
        editor.setSelection(range.index + 1);
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [2, 3, false] }],
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["blockquote", "link", "image", "video"],
                [{ align: [] }],
                ["clean"],
            ],
            handlers: {
                image: uploadImage,
                video: embedVideo,
            },
        },
        clipboard: { matchVisual: false },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    const formats = ["header", "bold", "italic", "underline", "list", "bullet", "blockquote", "link", "align", "image", "video"];

    return (
        <div data-testid={testid} className="rich-text-wrap">
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value || ""}
                onChange={onChange}
                modules={modules}
                formats={formats}
                placeholder="Write your story…"
            />
            <div className="mt-2 tracked text-coffee/45 text-[9px]">
                Tip: use the image / video buttons in the toolbar to insert media inline. Max 10 MB per file.
            </div>
            <style>{`
                .rich-text-wrap .ql-toolbar.ql-snow {
                    border: 1px solid rgba(26,15,0,0.2);
                    border-bottom: none;
                    background: #faf6ef;
                    font-family: 'DM Sans', sans-serif;
                }
                .rich-text-wrap .ql-container.ql-snow {
                    border: 1px solid rgba(26,15,0,0.2);
                    min-height: 320px;
                    background: white;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 15px;
                }
                .rich-text-wrap .ql-editor {
                    min-height: 320px;
                    line-height: 1.75;
                    color: #1a0f00;
                }
                .rich-text-wrap .ql-editor img { display: block; max-width: 100%; height: auto; margin: 12px auto; }
                .rich-text-wrap .ql-editor .quill-video, .rich-text-wrap .ql-editor iframe { display: block; width: 100%; max-width: 100%; aspect-ratio: 16/9; margin: 12px 0; }
                .rich-text-wrap .ql-editor.ql-blank::before {
                    color: rgba(26,15,0,0.4);
                    font-style: normal;
                }
                .rich-text-wrap .ql-snow .ql-stroke { stroke: #1a0f00; }
                .rich-text-wrap .ql-snow .ql-fill { fill: #1a0f00; }
                .rich-text-wrap .ql-snow .ql-picker { color: #1a0f00; }
            `}</style>
        </div>
    );
}
